<template>
  <b-standard-card
    :title="translations.title[lang]"
    :loading="loading"
    :error-message="errorMessage"
    :disabled="!invoicingFormValid"
    @submit="doUpdateInvoicing"
    :submit-text="{ el: 'Αποθήκευση', en: 'Save', it: 'Salva' }"
  >
    <v-row no-gutters>
      <v-col cols="12" sm="6" class="pr-0 pr-sm-2">
        <b-text-field
          v-model="invoicing.comp_name"
          :label="translations.name[lang]"
          :error-messages="formErrors.comp_name"
          @input="formErrors.comp_name = ''"
          @blur="validateField('comp_name')"
          no-top-margin
        ></b-text-field>
      </v-col>
      <v-col cols="12" sm="6" class="pl-0 pl-sm-2">
        <b-text-field
          v-model="invoicing.vat_number"
          :label="translations.taxId[lang]"
          :success="vatIsValid"
          :error-messages="vatError"
          :disabled="!invoicing.country_id"
          @focus="vatError = ''"
          @input="vatError = ''"
          no-top-margin
        ></b-text-field>
      </v-col>
      <v-col cols="12" sm="6" class="pr-0 pr-sm-2">
        <b-text-field
          v-model="invoicing.city"
          :label="translations.address[lang]"
          :error-messages="formErrors.city"
          @input="formErrors.city = ''"
          @blur="validateField('city')"
        ></b-text-field>
      </v-col>
      <v-col cols="12" sm="6" class="pl-0 pl-sm-2">
        <b-text-field
          v-model="invoicing.occupation"
          :label="translations.profession[lang]"
          :error-messages="formErrors.occupation"
          @input="formErrors.occupation = ''"
          @blur="validateField('occupation')"
        ></b-text-field>
      </v-col>
      <v-col cols="12" sm="6" class="pr-0 pr-sm-2">
        <b-text-field
          v-model="invoicing.tax_office"
          :label="translations.jurisdiction[lang]"
          :error-messages="formErrors.tax_office"
          @input="formErrors.tax_office = ''"
          @blur="validateField('tax_office')"
        ></b-text-field>
      </v-col>
      <v-col cols="12" sm="6" class="pl-0 pl-sm-2">
        <b-select
          v-model="invoicing.country_id"
          :items="countriesWithVat"
          item-value="country_id"
          :label="translations.invoice[lang]"
          readonly
        >
          <template #selection="{ item }">
            <template v-if="item && item.name">
              <v-img height="24" width="24" max-width="24" :src="item.flag" :alt="item.name" />
              <v-list-item-content class="py-0 px-3" style="flex: 1 0 auto">
                <v-list-item-title>
                  {{ item.name }}
                  <span v-if="item.vat_percentage">({{ item.vat_percentage }}%)</span>
                </v-list-item-title>
              </v-list-item-content>
            </template>
            <template v-else>
              <span></span>
            </template>
          </template>
          <template #item="{ item }">
            <template v-if="item && item.name">
              <v-img height="24" width="24" max-width="24" :src="item.flag" :alt="item.name" />
              <v-list-item-content class="py-0 px-3">
                <v-list-item-title>
                  {{ item.name }}
                  <span v-if="item.vat_percentage">({{ item.vat_percentage }}%)</span>
                </v-list-item-title>
              </v-list-item-content>
            </template>
            <template v-else>
              <span></span>
            </template>
          </template>
        </b-select>
      </v-col>
      <v-col md="6" class="pr-2">
        <v-col class="pa-0">
          <b-text-field
            label="Elorus Client ID"
            v-model="invoicing.elorus_client_id"
            :disabled="!isSuperAdmin"
          ></b-text-field>
          <v-btn
            v-if="isSuperAdmin"
            color="secondary"
            class="mt-2 text-capitalize"
            :loading="loadingElorus"
            @click="generateElorusClientId"
            >{{ translations.generateNewClientId[lang] }}</v-btn
          >
        </v-col>
      </v-col>
    </v-row>
  </b-standard-card>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import translations from '@/utils/translations/storePanel/settings/profile/invoicing';

export default {
  name: 'Invoicing',

  mixins: [translations],

  data: () => ({
    invoicing: {},
    errorMessages: {
      vatNumberRequired: {
        el: 'Απαιτείται Αριθμος ΑΦΜ',
        en: 'Vat Number is required',
        it: 'È richiesto il numero di partita IVA',
      },
      vatNumberLength: {
        el: 'Ο Αριθμός ΑΦΜ πρέπει να έχει από 9 εώς 12 χαρακτήρες',
        en: 'Vat Number must be from 9 to 12 characters long',
        it: 'Il numero di partita IVA deve avere una lunghezza compresa tra 9 e 12 caratteri.',
      },
      fieldRequired: {
        el: 'To πεδίο απαιτείται ',
        en: 'This field is required',
        it: 'Campo obbligatorio',
      },
    },
    vatIsValid: false,
    vatError: '',
    elorusId: '',
    formErrors: {
      comp_name: '',
      city: '',
      occupation: '',
      tax_office: '',
    },
  }),

  computed: {
    ...mapState('storePanel/profile', ['countries']),
    ...mapState(['store', 'isSuperAdmin']),
    lang() {
      return this.$route.params.lang;
    },

    loading() {
      return this.$store.state.storePanel.profile.loading.invoicing;
    },

    loadingElorus() {
      return this.$store.state.storePanel.profile.loading.elorus;
    },

    errorMessage() {
      return this.$store.state.storePanel.profile.errorMessage.invoicing;
    },
    countriesWithVat() {
      return this.countries.filter((country) => country.active && country.vat_percentage);
    },
    vatValidateKeys() {
      return {
        vat_number: this.invoicing.vat_number,
        country_id: this.invoicing.country_id,
      };
    },
    requiredFieldsPresent() {
      return (
        !!String(this.invoicing.comp_name || '').trim() &&
        !!String(this.invoicing.city || '').trim() &&
        !!String(this.invoicing.occupation || '').trim() &&
        !!String(this.invoicing.tax_office || '').trim()
      );
    },

    invoicingFormValid() {
      return this.requiredFieldsPresent && !!this.vatIsValid;
    },
  },

  methods: {
    ...mapActions(['checkVatNumber']),
    ...mapActions('storePanel/profile', [
      'updateInvoicing',
      'getCountries',
      'createElorusClientId',
    ]),
    ...mapMutations(['setNotification']),
    ...mapMutations('storePanel/profile', ['setErrorMessage']),

    async validateVatNumber() {
      this.vatError = '';
      let isValid = false;
      if (!this.invoicing.vat_number) {
        this.vatError = this.errorMessages.vatNumberRequired[this.lang];
        isValid = false;
      } else if (this.invoicing.vat_number.length < 9 || this.invoicing.vat_number.length > 12) {
        this.vatError = this.errorMessages.vatNumberLength[this.lang];
        isValid = false;
      } else if (this.invoicing.vat_number?.length >= 9) {
        let res = await this.checkVatNumber({
          afm: this.invoicing.vat_number,
          country_id: this.invoicing.country_id,
        });
        if (res.data?.status === 'success') {
          isValid = true;
        } else if (res.data?.error) {
          this.vatError = res.data?.message || '';
          isValid = false;
        }
      }
      return isValid;
    },

    showError(value) {
      this.setErrorMessage({
        value,
        type: 'invoicing',
      });
    },
    validateField(field) {
      const val = String(this.invoicing[field] || '').trim();
      if (!val) {
        this.$set(this.formErrors, field, this.errorMessages.fieldRequired[this.lang]);
      } else {
        this.$set(this.formErrors, field, '');
      }
    },
    async doUpdateInvoicing() {
      let res = await this.updateInvoicing({
        type: 'invoicing',
        item: this.invoicing,
      });
      if (res.status === 'error') {
        this.showError(res.message || 'Error');
      }
    },
    async generateElorusClientId() {
      const res = await this.createElorusClientId();
      if (res.status === 'success') {
        this.invoicing.elorus_client_id = res.data.elorus_client_id;
        console.log(this.invoicing.elorus_client_id);
        this.setNotification({
          show: true,
          type: 'success',
          text: this.translations.elorusCreatedSuccess[this.lang],
        });
      } else {
        this.showError(res.message || 'Error');
      }
    },
  },

  watch: {
    ['store.billing_details']: {
      deep: true,
      immediate: true,
      handler(val) {
        if (!val) return;
        this.invoicing = {
          ...this.invoicing,
          comp_name: val.comp_name || '',
          vat_number: val.vat_number || '',
          city: val.city || '',
          occupation: val.occupation || '',
          tax_office: val.tax_office || '',
          country_id: val.country_id || null,
          elorus_client_id: val.data?.elorus?.client_id || '',
        };
      },
    },

    vatValidateKeys: {
      deep: true,
      async handler(val, oldVal) {
        if (val.country_id == oldVal.country_id && val.vat_number == oldVal.vat_number) {
          return;
        }
        this.vatIsValid = val.country_id && val.vat_number ? await this.validateVatNumber() : false;
      },
    },

    store: {
      immediate: true,
      deep: true,
      async handler(val) {
        if (val) {
          this.invoicing.country_id = val.country?.country_id || val.billing_details?.country_id;
        }
      },
    },
  },
  mounted() {
    if (!this.countries?.length) {
      this.getCountries();
    }
  },
};
</script>
